import { riderChroma } from '../shared/rider';
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useSocket } from '../contexts/SocketContext';
import { roundClock } from '../game/clock';
import { useLiveMultiplier } from '../game/useLiveMultiplier';
import { BackgroundScroller }  from './entities/Background';
import { ObstacleManager, raceScrollSpeedForMultiplier } from './entities/ObstacleManager';
import { soundManager }        from './entities/SoundManager';
import { MultiSkierManager, STARTING_LINEUP_ENABLED } from './entities/MultiSkierManager';
import { deriveSeed }          from './entities/SkierEntity';
import type { CrashPresentation } from './entities/SkierEntity';
import { CrashEffectController } from './entities/CrashEffectController';
import { getAuroraEscalation, RunWeatherEffect } from './entities/RunWeatherEffect';
import { formatMoney } from '../utils/money';
import { buildReloadTrace } from '../utils/multiplierTrace';
import { DISPLAY_CONFIG } from '../config/display';
import { getNextRunTier, getRunTier, getRunTierProgress, RunTier } from '../utils/runTiers';
import { getCanvasLogicalSize } from '../utils/canvasSizing';
import { publishRoster, resetRoster } from '../game/riderRoster';
import type { GamePhase } from '../types/game.types';


interface CanvasGameProps {
  soloMode: boolean;
  showSlopeTrace?: boolean;
  showRunLevelGraph?: boolean;
  showRunLevelTitles?: boolean;
}

const TRACE_HEIGHT=80,TRACE_PAD_L=36,TRACE_PAD_R=8,TRACE_PAD_TOP=8,TRACE_PAD_BOT=6,MAX_TRACE_PTS=600,RENDER_PTS_MAX=220;
const STARTING_PHASE_MS=5000;
const ROUND_START_SPEED=raceScrollSpeedForMultiplier(1);
const PRE_ACCEL_START_SPEED=STARTING_LINEUP_ENABLED?0.35:0.65;

function startingPhaseSpeed(phaseStartTime?:number){
  const elapsed=Math.max(0,Date.now()-(phaseStartTime??Date.now()));
  const p=Math.max(0,Math.min(1,elapsed/STARTING_PHASE_MS));
  const ease=Math.pow(p,3.2);
  return PRE_ACCEL_START_SPEED+(ROUND_START_SPEED-PRE_ACCEL_START_SPEED)*ease;
}

function tierRGB(m:number):[number,number,number]{
  if(m<25)return getRunTier(m).rgb;
  const celebration=getAuroraEscalation(m);
  return celebration<.56
    ?lerpRGB([103,226,226],[255,218,139],celebration/.56)
    :lerpRGB([255,218,139],[220,184,255],(celebration-.56)/.44);
}
function lerpRGB(a:[number,number,number],b:[number,number,number],t:number):[number,number,number]{return[Math.round(a[0]+(b[0]-a[0])*t),Math.round(a[1]+(b[1]-a[1])*t),Math.round(a[2]+(b[2]-a[2])*t)];}
function traceY(m:number,mn:number,mx:number,dt:number,db:number){return db-Math.max(0,Math.min(1,(m-mn)/(mx-mn)))*(db-dt);}
function dynamicRefs(mn:number,mx:number){return[0.25,0.5,0.75].map(f=>{const r=mn+f*(mx-mn),mag=Math.pow(10,Math.floor(Math.log10(Math.max(r,0.1))));return Math.round(r/mag)*mag;});}

interface DevSkierRow{playerId:string;username:string;betAmount:number;status:'active'|'cashed_out';cashout:string;cashedOutAt?:number;}
interface CanvasDiagEvent{t:number;label:string;phase?:string;roundKey?:string|number|null;mult?:number;details?:Record<string,unknown>;}

const CRASH_EFFECT_OPTIONS:{id:CrashPresentation;emoji:string;label:string}[]=[
  {id:'avalanche',emoji:'🌨️',label:'Avalanche'},
  {id:'snow_slab',emoji:'🧊',label:'Snow slab'},
  {id:'cliff',emoji:'🕳️',label:'Cliff'},
  {id:'snowball',emoji:'⚪',label:'Snowball'},
  {id:'explosion',emoji:'💥',label:'Legacy'},
];

const ROUND_DIAG_STORAGE_KEY='whiteoutRoundDiag';
const isRoundDiagEnabled=()=>process.env.NODE_ENV==='development'||localStorage.getItem(ROUND_DIAG_STORAGE_KEY)==='true'||localStorage.getItem('slopeShakeDiag')==='true';

const roundKeyFromCurrentGame=(currentGame:Record<string,unknown>|null|undefined):string|number|null=>{
  const cg=currentGame as any;
  return cg?.id??cg?.gameNumber??null;
};
const roundNumberFromCurrentGame=(currentGame:Record<string,unknown>|null|undefined):number|null=>{
  const n=(currentGame as any)?.gameNumber;
  return typeof n==='number'?n:null;
};
const roundKeyFromGameInfo=(info:any):string|number|null=>roundKeyFromCurrentGame(info?.currentGame);
const roundKeyFromEvent=(data:any):string|number|null=>data?.gameId??data?.gameNumber??null;
const startingResetToken=(info:any):string=>`${roundKeyFromGameInfo(info)??'unknown'}:starting:${info?.phaseStartTime??'unknown'}`;
const startingEventResetToken=(data:any):string=>`${roundKeyFromEvent(data)??'unknown'}:starting:${data?.phaseStartTime??'unknown'}`;
const rosterIdsFromGameInfo=(info:any):string[]=>(info?.bets??[]).map((bet:any)=>bet?.playerId).filter((id:unknown):id is string=>typeof id==='string'&&id.length>0);
const isOlderRoundEvent=(currentInfo:any,data:any):boolean=>{
  const currentNumber=roundNumberFromCurrentGame(currentInfo?.currentGame);
  const incomingNumber=typeof data?.gameNumber==='number'?data.gameNumber:null;
  return currentNumber!==null&&incomingNumber!==null&&incomingNumber<currentNumber;
};
const eventMatchesRound=(currentInfo:any,data:any):boolean=>{
  const currentGame=currentInfo?.currentGame as any;
  if(data?.gameId!==undefined&&currentGame?.id!==undefined)return currentGame.id===data.gameId;
  if(data?.gameNumber!==undefined&&currentGame?.gameNumber!==undefined)return currentGame.gameNumber===data.gameNumber;
  return true;
};
const isSupersededCrashFrame=(info:any,visualRoundKey:string|number|null):boolean=>{
  return info?.phase==='CRASHED'&&visualRoundKey!==null&&roundKeyFromGameInfo(info)!==visualRoundKey;
};

const DevSkierAdmin:React.FC<{
  rows:DevSkierRow[];
  name:string;
  bet:string;
  currentMultiplier:number;
  onName:(v:string)=>void;
  onBet:(v:string)=>void;
  onAdd:()=>void;
  onCashout:(id:string,mult:number)=>void;
  onCashoutInput:(id:string,value:string)=>void;
  onClear:()=>void;
  minimized:boolean;
  onToggleMinimized:()=>void;
  crashPresentation:CrashPresentation;
  onCrashPresentation:(presentation:CrashPresentation)=>void;
  simulationActive:boolean;
  scenarioRunning:boolean;
  phase?:GamePhase;
  onEnterSimulation:()=>void;
  onExitSimulation:()=>void;
  onNewRound:()=>void;
  onPhase:(phase:GamePhase)=>void;
  onMultiplier:(multiplier:number)=>void;
  onTriggerCrash:()=>void;
  onRunLifecycle:()=>void;
  onLoadLateRun:()=>void;
  onStopScenario:()=>void;
  onRemove:(playerId:string)=>void;
}>=({rows,name,bet,currentMultiplier,onName,onBet,onAdd,onCashout,onCashoutInput,onClear,minimized,onToggleMinimized,crashPresentation,onCrashPresentation,simulationActive,scenarioRunning,phase,onEnterSimulation,onExitSimulation,onNewRound,onPhase,onMultiplier,onTriggerCrash,onRunLifecycle,onLoadLateRun,onStopScenario,onRemove})=>{
  const panel:React.CSSProperties={position:'relative',width:'100%',height:minimized?42:610,overflow:'hidden',pointerEvents:'auto',background:'rgba(4,8,14,0.94)',borderTop:'1px solid rgba(117,232,255,0.14)',fontFamily:"'JetBrains Mono',monospace",color:'rgba(230,240,255,0.88)',transition:'height 0.16s ease'};
  const input:React.CSSProperties={height:26,minWidth:0,background:'rgba(255,255,255,0.055)',border:'1px solid rgba(255,255,255,0.10)',borderRadius:5,color:'rgba(245,248,255,0.92)',padding:'0 7px',fontSize:'0.68rem',outline:'none'};
  const btn:React.CSSProperties={height:26,border:0,borderRadius:5,padding:'0 9px',fontSize:'0.66rem',fontWeight:800,letterSpacing:'0.06em',textTransform:'uppercase',cursor:'pointer'};
  return(
    <div style={panel}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',height:42,padding:'0 9px',borderBottom:minimized?'none':'1px solid rgba(255,255,255,0.08)'}}>
        <div>
          <div style={{fontSize:'0.64rem',letterSpacing:'0.16em',textTransform:'uppercase',color:'rgba(240,180,41,0.78)'}}>Visual debug</div>
          {!minimized&&<div style={{fontSize:'0.58rem',color:'rgba(150,160,180,0.74)',marginTop:2}}>Development only · local presentation tools</div>}
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <button type="button" onClick={onToggleMinimized} aria-label={minimized?'Restore visual debug':'Minimize visual debug'} title={minimized?'Restore':'Minimize'} style={{...btn,width:28,padding:0,background:'rgba(255,255,255,0.08)',color:'rgba(220,230,245,0.82)',fontSize:'0.8rem'}}>{minimized?'▢':'−'}</button>
        </div>
      </div>
      {!minimized&&<div style={{height:'calc(100% - 42px)',overflowY:'auto',scrollbarWidth:'thin'}}>
      <section style={{padding:'9px',borderBottom:'1px solid rgba(255,255,255,0.08)',background:simulationActive?'rgba(117,232,255,.045)':'transparent'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8}}>
          <div>
            <div style={{fontSize:'0.57rem',letterSpacing:'0.15em',textTransform:'uppercase',color:simulationActive?'#75e8ff':'rgba(150,165,185,0.78)'}}>Local simulation</div>
            <div style={{fontSize:'0.51rem',color:'rgba(150,160,180,0.68)',marginTop:3}}>{simulationActive?'Live socket quarantined · server actions blocked':'Enter an isolated canonical game state'}</div>
          </div>
          <button type="button" onClick={simulationActive?onExitSimulation:onEnterSimulation} style={{...btn,height:24,background:simulationActive?'rgba(255,88,100,.14)':'rgba(117,232,255,.14)',color:simulationActive?'#ff8790':'#75e8ff',border:`1px solid ${simulationActive?'rgba(255,88,100,.3)':'rgba(117,232,255,.3)'}`}}>{simulationActive?'Return live':'Isolate'}</button>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:5,marginTop:9}}>
          {(['STARTING','LAUNCHING','IN_PROGRESS','CRASHED'] as GamePhase[]).map(value=><button key={value} type="button" disabled={!simulationActive} onClick={()=>onPhase(value)} style={{...btn,height:24,padding:'0 4px',fontSize:'0.49rem',background:phase===value?'rgba(117,232,255,.16)':'rgba(255,255,255,.045)',color:phase===value?'#75e8ff':'rgba(210,222,236,.68)',border:phase===value?'1px solid rgba(117,232,255,.32)':'1px solid rgba(255,255,255,.07)'}}>{value.replace('_',' ')}</button>)}
        </div>
        <div style={{display:'grid',gridTemplateColumns:'88px 1fr',gap:6,marginTop:6}}>
          <input aria-label="Simulation multiplier" type="number" min="1" step="0.1" disabled={!simulationActive} value={currentMultiplier} onChange={event=>onMultiplier(Number(event.target.value))} style={input}/>
          <div style={{display:'flex',gap:4,overflowX:'auto'}}>{[1,1.5,2.5,5,10,25,40,75,100].map(value=><button key={value} type="button" disabled={!simulationActive} onClick={()=>onMultiplier(value)} style={{...btn,height:26,minWidth:38,padding:'0 5px',fontSize:'0.52rem',background:'rgba(255,255,255,.05)',color:'rgba(220,232,244,.72)'}}>{value}x</button>)}</div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:5,marginTop:6}}>
          <button type="button" disabled={!simulationActive} onClick={onNewRound} style={{...btn,height:24,padding:'0 4px',fontSize:'0.5rem',background:'rgba(255,255,255,.055)',color:'rgba(220,232,244,.74)'}}>New round</button>
          <button type="button" disabled={!simulationActive} onClick={onTriggerCrash} style={{...btn,height:24,padding:'0 4px',fontSize:'0.5rem',background:'rgba(255,88,100,.12)',color:'#ff8790'}}>Crash</button>
          <button type="button" disabled={!simulationActive||scenarioRunning} onClick={onRunLifecycle} style={{...btn,height:24,padding:'0 4px',fontSize:'0.5rem',background:'rgba(156,255,87,.1)',color:'#9cff57'}}>Lifecycle</button>
          <button type="button" disabled={!simulationActive} onClick={scenarioRunning?onStopScenario:onLoadLateRun} style={{...btn,height:24,padding:'0 4px',fontSize:'0.5rem',background:'rgba(240,180,41,.1)',color:'#f0b429'}}>{scenarioRunning?'Stop':'Late run'}</button>
        </div>
      </section>
      <section style={{padding:'9px',borderBottom:'1px solid rgba(255,255,255,0.08)'}}>
        <div style={{fontSize:'0.57rem',letterSpacing:'0.15em',textTransform:'uppercase',color:'rgba(150,165,185,0.78)',marginBottom:7}}>Crash presentation</div>
        <div style={{display:'flex',gap:6,overflowX:'auto',paddingBottom:3}}>
          {CRASH_EFFECT_OPTIONS.map(option=>{
            const selected=option.id===crashPresentation;
            return <button key={option.id} type="button" onClick={()=>onCrashPresentation(option.id)} title={option.label} aria-pressed={selected} style={{width:58,minWidth:58,height:54,borderRadius:6,cursor:'pointer',border:selected?'1px solid rgba(0,216,127,0.72)':'1px solid rgba(255,255,255,0.10)',background:selected?'rgba(0,216,127,0.14)':'rgba(255,255,255,0.045)',color:selected?'#8fffc5':'rgba(220,230,245,0.72)',boxShadow:selected?'0 0 12px rgba(0,216,127,0.14)':'none'}}>
              <span style={{display:'block',fontSize:'1.18rem',lineHeight:1.15}}>{option.emoji}</span>
              <span style={{display:'block',fontSize:'0.48rem',marginTop:4,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{option.label}</span>
            </button>;
          })}
        </div>
        <div style={{fontSize:'0.52rem',color:'rgba(150,160,180,0.58)',marginTop:5}}>Selected effect applies to the next simulated or live crash.</div>
      </section>
      <section>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',height:30,padding:'0 9px',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{fontSize:'0.57rem',letterSpacing:'0.15em',textTransform:'uppercase',color:'rgba(150,165,185,0.78)'}}>Skier lab</div>
        <button type="button" onClick={onClear} style={{...btn,height:22,background:'rgba(255,255,255,0.08)',color:'rgba(220,230,245,0.78)'}}>Clear</button>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 76px 54px',gap:6,padding:9}}>
        <input aria-label="Dev skier name" value={name} onChange={e=>onName(e.target.value)} placeholder="Name" style={input}/>
        <input aria-label="Dev skier bet" value={bet} onChange={e=>onBet(e.target.value)} placeholder="Bet" inputMode="decimal" style={input}/>
        <button type="button" onClick={onAdd} disabled={!simulationActive} title={simulationActive?'Add to simulated canonical roster':'Enter local simulation first'} style={{...btn,background:simulationActive?'rgba(0,216,127,0.20)':'rgba(255,255,255,.045)',color:simulationActive?'#00d87f':'rgba(150,160,180,.45)',border:'1px solid rgba(0,216,127,0.22)'}}>Add</button>
      </div>
      <div style={{height:129,overflowY:'auto',borderTop:'1px solid rgba(255,255,255,0.07)'}}>
        {rows.length===0?(
          <div style={{height:129,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.66rem',color:'rgba(150,160,180,0.8)',textAlign:'center'}}>No dev skiers</div>
        ):rows.map(row=>{
          const parsed=Number(row.cashout);
          const mult=Number.isFinite(parsed)&&parsed>0?parsed:currentMultiplier;
          return(
            <div key={row.playerId} style={{display:'grid',gridTemplateColumns:'1fr 48px 52px 62px 24px',gap:5,alignItems:'center',padding:'6px 9px',borderBottom:'1px solid rgba(255,255,255,0.055)',fontSize:'0.66rem'}}>
              <div style={{minWidth:0}}>
                <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',color:'rgba(240,248,255,0.9)'}}>{row.username}</div>
                <div style={{fontSize:'0.56rem',color:row.status==='active'?'rgba(0,216,127,0.72)':'rgba(240,180,41,0.72)'}}>{row.status==='active'?'active':`out @ ${row.cashedOutAt?.toFixed(2)}x`}</div>
              </div>
              <div style={{textAlign:'right',color:'#f0b429'}}>{formatMoney(row.betAmount)}</div>
              <input aria-label={`Cashout ${row.username}`} value={row.cashout} onChange={e=>onCashoutInput(row.playerId,e.target.value)} disabled={row.status!=='active'} style={{...input,height:24,padding:'0 5px',fontSize:'0.62rem'}}/>
              <button type="button" disabled={row.status!=='active'} onClick={()=>onCashout(row.playerId,mult)} style={{...btn,height:24,background:row.status==='active'?'rgba(240,180,41,0.18)':'rgba(255,255,255,0.05)',color:row.status==='active'?'#f0b429':'rgba(150,160,180,0.55)',border:'1px solid rgba(240,180,41,0.26)',cursor:row.status==='active'?'pointer':'default'}}>Cashout</button>
              <button type="button" onClick={()=>onRemove(row.playerId)} aria-label={`Remove ${row.username}`} style={{...btn,width:24,height:24,padding:0,background:'rgba(255,88,100,.1)',color:'#ff8790'}}>×</button>
            </div>
          );
        })}
      </div>
      </section>
      </div>}
    </div>
  );
};

const RunTelemetry:React.FC<{
  phase?:string;
  hasActiveBet:boolean;
  hasCashedOut:boolean;
  cashoutMultiplier:number|null;
  activeBetAmt:number;
  startingAt:number|null;
  showRunLevelGraph:boolean;
}>=({phase,hasActiveBet,hasCashedOut,cashoutMultiplier,activeBetAmt,startingAt,showRunLevelGraph})=>{
  // Read here rather than in CanvasGame so only this readout re-renders on each
  // tick; it is the same prediction the canvas draws, so the live return agrees
  // with the skier. Settled values still come from the server.
  const currentMultiplier=useLiveMultiplier();
  const[now,setNow]=useState(Date.now());
  useEffect(()=>{if(phase!=='STARTING')return;const id=setInterval(()=>setNow(Date.now()),100);return()=>clearInterval(id);},[phase]);

  if(phase==='STARTING'){
    const elapsed=startingAt?(now-startingAt)/1000:0;
    const timeLeft=Math.max(0,5-elapsed);
    return <div className="run-telemetry is-countdown"><span>Next run</span><strong>{timeLeft>0?`${Math.ceil(timeLeft)}s`:'Starting…'}</strong></div>;
  }
  if(phase==='LAUNCHING')return <div className="run-telemetry is-launching"><span>Launch sequence</span><strong>Drop in</strong></div>;

  const tier=getRunTier(currentMultiplier);
  const nextTier=getNextRunTier(currentMultiplier);
  const auroraMilestone=currentMultiplier>=100
    ?{name:'Legendary',min:100,next:null as number|null,nextName:'Beyond measure'}
    :currentMultiplier>=75
      ?{name:'Ascendant',min:75,next:100,nextName:'Legendary'}
      :currentMultiplier>=40
        ?{name:'Summit run',min:40,next:75,nextName:'Ascendant'}
        :{name:'Rare air',min:25,next:40,nextName:'Summit run'};
  const displayName=tier.id==='aurora'?auroraMilestone.name:tier.name;
  const displayMin=tier.id==='aurora'?auroraMilestone.min:tier.min;
  const displayNext=tier.id==='aurora'?auroraMilestone.next:nextTier?.min??null;
  const progress=tier.id==='aurora'
    ?displayNext===null?1:Math.max(0,Math.min(1,(currentMultiplier-displayMin)/(displayNext-displayMin)))
    :getRunTierProgress(currentMultiplier);
  const securedMultiplier=cashoutMultiplier??currentMultiplier;

  if(phase==='CRASHED'){
    if(hasActiveBet&&hasCashedOut)return <div className="run-telemetry is-result is-won"><span>Run secured</span><strong>{securedMultiplier.toFixed(2)}x · {formatMoney(activeBetAmt*securedMultiplier)}</strong></div>;
    if(hasActiveBet)return <div className="run-telemetry is-result is-lost"><span>Whiteout · {currentMultiplier.toFixed(2)}x</span><strong>Lost {formatMoney(activeBetAmt)}</strong></div>;
    return <div className="run-telemetry is-result is-lost"><span>Run ended</span><strong>Whiteout · {currentMultiplier.toFixed(2)}x</strong></div>;
  }
  if(!showRunLevelGraph)return <div className="run-telemetry is-quiet"><span>Run in progress</span></div>;

  return <div className={`run-telemetry tier-${tier.id}`} style={{'--tier-color':tier.color,'--tier-progress':`${progress*100}%`} as React.CSSProperties}>
    <div className="telemetry-level"><span>Run level {String(tier.index+1).padStart(2,'0')}</span><strong>{displayName}</strong></div>
    <div className="telemetry-meter" aria-label={`${displayName} run level, ${Math.round(progress*100)} percent to next level`}>
      <div className="telemetry-track"><span /></div>
      <div className="telemetry-scale"><span>{displayMin.toFixed(2)}x</span><span>{tier.id==='aurora'?(displayNext?`${displayNext.toFixed(2)}x · ${auroraMilestone.nextName}`:auroraMilestone.nextName):(nextTier?`${nextTier.min.toFixed(2)}x · ${nextTier.name}`:'Beyond the storm')}</span></div>
    </div>
    <div className="telemetry-value"><span>{hasCashedOut?'Secured':hasActiveBet?'Live return':'Multiplier'}</span><strong>{hasCashedOut?`${securedMultiplier.toFixed(2)}x`:hasActiveBet?formatMoney(activeBetAmt*currentMultiplier):`${currentMultiplier.toFixed(2)}x`}</strong></div>
  </div>;
};

/** Speed-graph band: 80 px on the tablet/desktop worlds, but never more than 17% of a short phone canvas. */
const traceHeightFor=(H:number)=>Math.min(TRACE_HEIGHT,Math.round(H*.17));

/** Headline font that shrinks only when the text would overflow (e.g. "WHITEOUT @ 12.34x" on a phone). */
function fitFont(ctx:CanvasRenderingContext2D,text:string,basePx:number,maxWidth:number){
  ctx.font=`bold ${basePx}px Barlow Condensed,Arial`;
  const width=ctx.measureText(text).width;
  return width<=maxWidth?ctx.font:`bold ${Math.max(18,Math.floor(basePx*maxWidth/width))}px Barlow Condensed,Arial`;
}

const CanvasGame:React.FC<CanvasGameProps>=({ soloMode, showSlopeTrace = true, showRunLevelGraph = true, showRunLevelTitles = true })=>{
  const canvasRef=useRef<HTMLCanvasElement>(null);
  const viewportRef=useRef<HTMLDivElement>(null);
  const canvasProfileRef=useRef<'mobile'|'mobile-wide'|'tablet'|'desktop'|null>(null);
  const{socket,player,currentMultiplier,gameInfo,provablyFair,myBet,devSimulation,friends}=useSocket();
  // Held in a ref so the visibility handler, which is registered once, always
  // reaches the current socket rather than the one captured at mount.
  const socketRef=useRef<typeof socket>(socket);
  useEffect(()=>{socketRef.current=socket;},[socket]);
  const bgRef=useRef<BackgroundScroller|null>(null);
  const obsRef=useRef<ObstacleManager|null>(null);
  const msRef=useRef<MultiSkierManager|null>(null);
  const crashEffectRef=useRef<CrashEffectController|null>(null);
  const weatherRef=useRef<RunWeatherEffect|null>(null);
  const afRef=useRef<number|null>(null);
  const roundStartTimeRef=useRef<number|null>(null);
  const crashRef=useRef(false),flashRef=useRef(0);
  const shS=useRef(0),shD=useRef(0),shFr=useRef(30),shFd=useRef(0.25),shL=useRef(0),shFng=useRef(false);
  const shA=useRef(0),sh=useRef(0),shOff=useRef({x:0,y:0}),shTarg=useRef({x:0,y:0});
  const winnerAlpha=useRef(0),showingWinner=useRef(false);
  const postCrashTimer=useRef<ReturnType<typeof setTimeout>|null>(null),postCrashClean=useRef(false);
  const postCrashRoundKeyRef=useRef<string|number|null>(null);
  const launchTriggeredRef=useRef(false);
  const visualRoundKeyRef=useRef<string|number|null>(null);
  const visualResetTokenRef=useRef<string|number|null>(null);
  const lmRef=useRef(1),lmcRef=useRef(performance.now());
  const[startingAt,setStartingAt]=useState<number|null>(null);
  const stRef=useRef<number|null>(null);
  const sndRef=useRef(soundManager),giRef=useRef(gameInfo),mRef=useRef(currentMultiplier),lfRef=useRef(0);
  const trData=useRef<{t:number;m:number}[]>([]),trOp=useRef(0),trStart=useRef<number|null>(null),trLastM=useRef(0),trYMaxRef=useRef(2.0);
  const seedHashRef=useRef(provablyFair.serverSeedHash??'');
  const isDevAdmin=DISPLAY_CONFIG.showDevTools;
  const devSeqRef=useRef(1);
  const diagEventsRef=useRef<CanvasDiagEvent[]>([]);
  const[devRows,setDevRows]=useState<DevSkierRow[]>([]);
  const[devName,setDevName]=useState('DevSkier');
  const[devBet,setDevBet]=useState('100');
  const[devPanelMinimized,setDevPanelMinimized]=useState(true);
  const[devCrashPresentation,setDevCrashPresentation]=useState<CrashPresentation>('avalanche');
  const[tierTransition,setTierTransition]=useState<{tier:RunTier;key:number}|null>(null);
  const previousTierIndexRef=useRef<number|null>(null);
  const previousAuroraMilestoneRef=useRef(0);
  const tierTransitionTimerRef=useRef<ReturnType<typeof setTimeout>|null>(null);
  const tierImpulseRef=useRef(0);
  const reducedMotionRef=useRef(typeof window!=='undefined'&&window.matchMedia?.('(prefers-reduced-motion: reduce)').matches===true);
  useEffect(()=>{
    const media=window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if(!media)return;
    const sync=()=>{reducedMotionRef.current=media.matches;if(media.matches){sh.current=0;flashRef.current=0;tierImpulseRef.current=0;}};
    sync();media.addEventListener('change',sync);
    return()=>media.removeEventListener('change',sync);
  },[]);
  const devCrashPresentationRef=useRef<CrashPresentation>('avalanche');
  const activeCrashPresentationRef=useRef<CrashPresentation>('avalanche');

  const playerRef=useRef(player);
  useEffect(()=>{playerRef.current=player;},[player]);

  const friendsModeRef=useRef(!!friends?.code);
  friendsModeRef.current=!!friends?.code;
  const soloModeRef=useRef(soloMode);
  useEffect(()=>{ soloModeRef.current=soloMode; },[soloMode]);
  const showSlopeTraceRef=useRef(showSlopeTrace);
  useEffect(()=>{ showSlopeTraceRef.current=showSlopeTrace; },[showSlopeTrace]);

  const selectDevCrashPresentation=useCallback((presentation:CrashPresentation)=>{
    devCrashPresentationRef.current=presentation;
    setDevCrashPresentation(presentation);
  },[]);

  // React state now changes once a second (the server's correction), so the
  // animation reads the predicted value from the round clock every frame instead.
  // Without this the skier would advance in one-second steps.
  useEffect(()=>{giRef.current=gameInfo;if(currentMultiplier!==lmRef.current){lmRef.current=currentMultiplier;lmcRef.current=performance.now();}},[gameInfo,currentMultiplier]);
  const frameMultiplier=useCallback(()=>{
    const phase=giRef.current?.phase;
    if(phase==='IN_PROGRESS'&&roundClock.isRunning)return roundClock.multiplier();
    return mRef.current;
  },[]);
  useEffect(()=>{mRef.current=currentMultiplier;},[currentMultiplier]);
  useEffect(()=>{seedHashRef.current=provablyFair.serverSeedHash??'';},[provablyFair.serverSeedHash]);
  useEffect(()=>{if(gameInfo?.phase==='STARTING'){if(stRef.current===null){const t=Date.now();stRef.current=t;setStartingAt(t);}}else{stRef.current=null;setStartingAt(null);}},[gameInfo?.phase]);
  useEffect(()=>{
    if(gameInfo?.phase!=='IN_PROGRESS'){
      previousTierIndexRef.current=null;
      previousAuroraMilestoneRef.current=0;
      setTierTransition(null);
      return;
    }
    const tier=getRunTier(currentMultiplier);
    const previous=previousTierIndexRef.current;
    if(previous!==null&&tier.index>previous){
      setTierTransition({tier,key:Date.now()});
      tierImpulseRef.current=reducedMotionRef.current?0:Math.min(5.5,1.4+tier.index*.7);
      sndRef.current.playRunTierTransition(tier.index);
      if(tierTransitionTimerRef.current)clearTimeout(tierTransitionTimerRef.current);
      tierTransitionTimerRef.current=setTimeout(()=>setTierTransition(null),900);
    }
    if(tier.id==='aurora'){
      const milestone=[100,75,40].find(value=>currentMultiplier>=value)??25;
      if(previous!==null&&milestone>previousAuroraMilestoneRef.current&&milestone>25)sndRef.current.playAuroraMilestone(milestone);
      previousAuroraMilestoneRef.current=milestone;
    }
    previousTierIndexRef.current=tier.index;
  },[gameInfo?.phase,currentMultiplier]);
  useEffect(()=>()=>{if(tierTransitionTimerRef.current)clearTimeout(tierTransitionTimerRef.current);},[]);
  useEffect(()=>{
    if(gameInfo?.phase==='STARTING') msRef.current?.prepareForLaunchLineup();
    if(gameInfo?.phase==='LAUNCHING'){
      launchTriggeredRef.current=false;
    }
  },[gameInfo?.phase,gameInfo?.phaseStartTime]);

  useEffect(()=>{const hash=provablyFair.serverSeedHash;if(!hash)return;obsRef.current?.initSeed(hash);msRef.current?.initRound(hash);},[provablyFair.serverSeedHash]);


  useEffect(()=>{
    if(!msRef.current)return;
    msRef.current.setSoloFilter(soloMode && player?.id ? player.id : null);
  },[soloMode,player?.id]);

  const recordDiag=useCallback((label:string,details:Record<string,unknown>={})=>{
    if(!isRoundDiagEnabled())return;
    const info=giRef.current;
    const event:CanvasDiagEvent={t:Date.now(),label,phase:info?.phase,roundKey:roundKeyFromGameInfo(info),mult:mRef.current,details};
    diagEventsRef.current=[...diagEventsRef.current.slice(-29),event];
    (window as any).__whiteoutRoundEvents=diagEventsRef.current;
    console.info(`[WHITEOUT round] ${label}`,event);
  },[]);

  useEffect(()=>{recordDiag('react:gameInfo',{phase:gameInfo?.phase,roundKey:roundKeyFromGameInfo(gameInfo),bets:gameInfo?.bets?.length??0,cashouts:gameInfo?.cashouts?.length??0});},[gameInfo,recordDiag]);

  const logShakeDiag=useCallback((source:string,details:Record<string,unknown>={})=>{
    if(!isRoundDiagEnabled())return;
    const info=giRef.current;
    const recentEvents=diagEventsRef.current.slice(-16);
    const snapshot={
      source,
      phase:info?.phase,
      roundKey:roundKeyFromGameInfo(info),
      currentMultiplier:mRef.current,
      visibility:document.visibilityState,
      crashRef:crashRef.current,
      postCrashClean:postCrashClean.current,
      postCrashRoundKey:postCrashRoundKeyRef.current,
      visualRoundKey:visualRoundKeyRef.current,
      visualResetToken:visualResetTokenRef.current,
      obstacleCount:obsRef.current?.getCount(),
      skierCounts:{
        total:msRef.current?.getTotalCount(),
        visible:msRef.current?.getVisibleCount(),
        queue:msRef.current?.getQueueCount(),
      },
      bets:info?.bets?.map((b:any)=>({playerId:b.playerId,gameId:b.gameId,gameNumber:b.gameNumber,amount:b.amount})),
      cashouts:info?.cashouts?.map((c:any)=>({playerId:c.playerId,gameId:c.gameId,gameNumber:c.gameNumber,multiplier:c.multiplier})),
      details,
      recentEvents,
    };
    console.warn('[WHITEOUT round diagnostic] shake started',{
      source,
      phase:snapshot.phase,
      roundKey:snapshot.roundKey,
      visualRoundKey:snapshot.visualRoundKey,
      multiplier:snapshot.currentMultiplier,
      skierCounts:snapshot.skierCounts,
      details,
      recentEvents:recentEvents.slice(-6).map(({t,label,phase,roundKey,mult})=>({t,label,phase,roundKey,mult})),
      full:'window.__slopeLastShakeDiag',
    });
    (window as any).__slopeLastShakeDiag=snapshot;
    (window as any).__slopeShakeDiags=[...((window as any).__slopeShakeDiags??[]).slice(-9),snapshot];
  },[]);

  const resetRoundVisuals=useCallback((roundKey:string|number|null=null,resetToken:string|number|null=roundKey)=>{
    recordDiag('resetRoundVisuals:start',{roundKey,resetToken,previousResetToken:visualResetTokenRef.current});
    if(resetToken!==null&&visualResetTokenRef.current===resetToken)return false;
    if(!msRef.current||!obsRef.current||!bgRef.current)return false;
    visualRoundKeyRef.current=roundKey;
    visualResetTokenRef.current=resetToken;
    crashRef.current=false;winnerAlpha.current=0;showingWinner.current=false;postCrashClean.current=false;roundStartTimeRef.current=null;
    postCrashRoundKeyRef.current=null;
    launchTriggeredRef.current=false;
    flashRef.current=1.0;lfRef.current=0;
    sh.current=0;shOff.current={x:0,y:0};
    if(postCrashTimer.current){clearTimeout(postCrashTimer.current);postCrashTimer.current=null;}
    msRef.current?.reset();obsRef.current?.clear();crashEffectRef.current?.reset();weatherRef.current?.reset();sndRef.current.resetRunTier();bgRef.current?.setSpeedImmediate(PRE_ACCEL_START_SPEED);bgRef.current?.resume();bgRef.current?.resetScrollTotal();
    msRef.current?.prepareForLaunchLineup();
    setDevRows([]);
    trData.current=[];trOp.current=0;trStart.current=null;trLastM.current=0;trYMaxRef.current=2.0;lmRef.current=1;lmcRef.current=performance.now();
    recordDiag('resetRoundVisuals:done',{roundKey,resetToken});
    return true;
  },[recordDiag]);

  const clearCrashArtifacts=useCallback(()=>{
    recordDiag('clearCrashArtifacts');
    crashRef.current=false;postCrashClean.current=false;postCrashRoundKeyRef.current=null;showingWinner.current=false;winnerAlpha.current=0;
    sh.current=0;shOff.current={x:0,y:0};flashRef.current=0;
    crashEffectRef.current?.reset();
    if(postCrashTimer.current){clearTimeout(postCrashTimer.current);postCrashTimer.current=null;}
  },[recordDiag]);

  const hydrateTrace=useCallback((info:any,multiplier:number)=>{
    if(info?.phase!=='IN_PROGRESS')return;
    const cg=info.currentGame as any;
    const startTime=Number(cg?.startTime??info.phaseStartTime);
    if(!Number.isFinite(startTime)||startTime<=0)return;
    const elapsedSeconds=Math.max(0,(Date.now()-startTime)/1000);
    const current=Math.max(1,Number.isFinite(multiplier)?multiplier:1);
    trData.current=buildReloadTrace(elapsedSeconds,current);
    trStart.current=performance.now()-elapsedSeconds*1000;
    trLastM.current=current;
    trYMaxRef.current=Math.max(2,current*1.15);
    trOp.current=1;
    roundStartTimeRef.current=startTime;
  },[]);

  const hydrateRoundVisuals=useCallback((info:any,reconnect=true)=>{
    if(!info||!msRef.current)return;
    const cg=info.currentGame as any;
    const serverSeedHash=cg?.serverSeedHash??cg?.gameHash??seedHashRef.current??'';
    // Hydration also runs when bets and cashouts change. Re-seeding an already
    // active round clears ObstacleManager's live field, so only initialize the
    // visual seed when the canonical round seed actually changes.
    if(serverSeedHash&&serverSeedHash!==seedHashRef.current){
      obsRef.current?.initSeed(serverSeedHash);
      msRef.current.initRound(serverSeedHash);
      seedHashRef.current=serverSeedHash;
    }

    const myPlayerId=playerRef.current?.id;
    msRef.current.setFriendsMode(friendsModeRef.current);
    const bets=info.bets??[];
    const cashouts=info.cashouts??[];

    for(const co of cashouts) msRef.current.onCashout(co.playerId,co.multiplier,co);
    for(const bet of bets){
      const playerId=bet.playerId;
      if(!playerId)continue;
      const isMe=myPlayerId&&playerId===myPlayerId;
      const isNpc=typeof playerId==='string'&&playerId.startsWith('NPC_');
      msRef.current.addPlayer({
        playerId,
        username:bet.username??(isMe?(playerRef.current?.username??'You'):''),
        betAmount:bet.amount??0,
        chroma:isNpc?4:riderChroma(bet.chroma,playerId),
        prnSeed:deriveSeed(serverSeedHash,playerId),
        reconnect,
        isSelf:!!isMe,
      });
    }

    if(info.spotlight !== undefined) msRef.current.setSpotlight(info.spotlight);
    if(info.phase==='STARTING')msRef.current.prepareForLaunchLineup();
    if(info.phase==='LAUNCHING')msRef.current.beginLaunch();
    msRef.current.setSoloFilter(soloModeRef.current&&myPlayerId?myPlayerId:null);
  },[]);

  useEffect(()=>{
    if(!gameInfo)return;
    const phase=gameInfo.phase;
    const key=roundKeyFromGameInfo(gameInfo)??(phase==='STARTING'?`starting:${gameInfo.phaseStartTime}`:null);

    if(phase==='STARTING'){
      const didReset=resetRoundVisuals(key,startingResetToken(gameInfo));
      hydrateRoundVisuals(gameInfo,didReset);
      msRef.current?.prepareForLaunchLineup();
    }else if(phase==='LAUNCHING'){
      clearCrashArtifacts();
      hydrateRoundVisuals(gameInfo,true);
      launchTriggeredRef.current=false;
    }else if(phase==='IN_PROGRESS'){
      clearCrashArtifacts();
      hydrateRoundVisuals(gameInfo,true);
      if(trData.current.length<2)hydrateTrace(gameInfo,mRef.current);
    }else if(phase==='CRASHED' && gameInfo.spotlight !== undefined){
      // A final result is self-contained, including for a post-crash reconnect.
      // Do not respawn already-lost racers just to reconstruct the banner.
      msRef.current?.setSpotlight(gameInfo.spotlight);
    }
  },[gameInfo,resetRoundVisuals,hydrateRoundVisuals,hydrateTrace,clearCrashArtifacts]);

  useEffect(()=>{
    if(!socket)return;

    const onInit=(data:any)=>{
      recordDiag('socket:game:init',{incomingPhase:data.gameInfo?.phase,roundKey:roundKeyFromGameInfo(data.gameInfo)});
      if(!msRef.current||!obsRef.current)return;
      const phase=data.gameInfo?.phase, cg=data.gameInfo?.currentGame;
      const serverSeedHash=cg?.serverSeedHash??cg?.gameHash??seedHashRef.current??'';

      if(serverSeedHash){
        obsRef.current.initSeed(serverSeedHash);
        msRef.current.initRound(serverSeedHash);
        seedHashRef.current=serverSeedHash;
      }

      if(phase==='STARTING') resetRoundVisuals(roundKeyFromCurrentGame(cg)??`starting:${data.gameInfo?.phaseStartTime??Date.now()}`,startingResetToken(data.gameInfo));

      if(phase==='IN_PROGRESS'&&cg?.startTime){
        roundStartTimeRef.current=cg.startTime;
        const elapsedSeconds=(Date.now()-cg.startTime)/1000;
        obsRef.current.reconstituteAtElapsed(elapsedSeconds);
      }
      if(phase==='IN_PROGRESS')hydrateTrace(data.gameInfo,data.currentMultiplier??mRef.current);

      if(phase==='STARTING') msRef.current.prepareForLaunchLineup();
      if(phase==='LAUNCHING'){
        launchTriggeredRef.current=false;
      }

      if(phase!=='IN_PROGRESS'&&phase!=='STARTING'&&phase!=='LAUNCHING')return;

      const myPlayerId=data.player?.id;
      const bets=data.gameInfo?.bets??[];
      const cashouts=data.gameInfo?.cashouts??[];

      for(const co of cashouts) msRef.current.onCashout(co.playerId,co.multiplier,co);

      for(const bet of bets){
        const isMe=myPlayerId&&bet.playerId===myPlayerId;
        const isNpc=typeof bet.playerId==='string'&&bet.playerId.startsWith('NPC_');
        msRef.current.addPlayer({
          playerId:  bet.playerId,
          username:  bet.username??'',
          betAmount: bet.amount,
          chroma:    isNpc?4:riderChroma(bet.chroma,bet.playerId),
          prnSeed:   deriveSeed(serverSeedHash,bet.playerId),
          reconnect: true,
          isSelf:    !!isMe,
        });
      }

      if(phase==='LAUNCHING') msRef.current.beginLaunch();

      msRef.current.setSoloFilter(soloModeRef.current && myPlayerId ? myPlayerId : null);
    };

    const onBetSuccess=(data:any)=>{
      recordDiag('socket:game:bet:success',{playerId:data?.bet?.playerId,gameId:data?.bet?.gameId,gameNumber:data?.bet?.gameNumber});
      if(!msRef.current)return;
      const phase=giRef.current?.phase;
      if(phase==='CRASHED')return;
      const bet = data?.bet ?? {};
      if(!eventMatchesRound(giRef.current,bet))return;
      const playerId = bet.playerId ?? playerRef.current?.id;
      if(!playerId)return;
      msRef.current.addPlayer({
        playerId,username:bet.username??playerRef.current?.username??'You',
        betAmount:bet.amount??0,chroma:riderChroma(bet.chroma,playerId),
        prnSeed:deriveSeed(seedHashRef.current,playerId),
        reconnect:false,
        isSelf:true,
      });
    };

    const onBetPlaced=(data:any)=>{
      recordDiag('socket:game:bet:placed',{playerId:data?.playerId,gameId:data?.gameId,gameNumber:data?.gameNumber});
      if(!msRef.current)return;
      const phase=giRef.current?.phase;
      if(phase==='CRASHED')return;
      if(!eventMatchesRound(giRef.current,data))return;
      const isNpc=typeof data.playerId==='string'&&data.playerId.startsWith('NPC_');
      msRef.current.addPlayer({
        playerId:data.playerId,username:data.username??'',
        betAmount:data.amount,
        chroma:isNpc?4:riderChroma(data.chroma,data.playerId),
        prnSeed:deriveSeed(seedHashRef.current,data.playerId),
        reconnect:false,
        isSelf:!!playerRef.current?.id&&data.playerId===playerRef.current.id,
      });
    };

    const onStarting=(data:any)=>{
      recordDiag('socket:game:starting',{gameId:data?.gameId,gameNumber:data?.gameNumber});
      if(isOlderRoundEvent(giRef.current,data))return;
      resetRoundVisuals(roundKeyFromEvent(data)??`starting:${data?.phaseStartTime??Date.now()}`,startingEventResetToken(data));
    };
    const onStarted=(data:any)=>{
      recordDiag('socket:game:started',{gameId:data?.gameId,gameNumber:data?.gameNumber,startTime:data?.startTime});
      if(isOlderRoundEvent(giRef.current,data))return;
      if(data?.startTime)roundStartTimeRef.current=data.startTime;
    };
    const onLaunching=(data:any)=>{
      recordDiag('socket:game:launching',{gameId:data?.gameId,gameNumber:data?.gameNumber});
      if(isOlderRoundEvent(giRef.current,data))return;
      launchTriggeredRef.current=false;
      msRef.current?.beginLaunch();
    };
    const onCashoutDone=(data:any)=>{
      recordDiag('socket:game:cashout:done',{playerId:data?.playerId,gameId:data?.gameId,gameNumber:data?.gameNumber,multiplier:data?.multiplier});
      const phase=giRef.current?.phase;
      if(!eventMatchesRound(giRef.current,data))return;
      if(phase!=='IN_PROGRESS'&&phase!=='CRASHED')return;
      msRef.current?.onCashout(data.playerId,data.multiplier,data);
    };

    socket.on('game:init',         onInit);
    socket.on('game:bet:success',  onBetSuccess);
    socket.on('game:bet:placed',   onBetPlaced);
    socket.on('game:starting',     onStarting);
    socket.on('game:started',      onStarted);
    socket.on('game:launching',    onLaunching);
    socket.on('game:cashout:done', onCashoutDone);
    return()=>{
      socket.off('game:init',         onInit);
      socket.off('game:bet:success',  onBetSuccess);
      socket.off('game:bet:placed',   onBetPlaced);
      socket.off('game:starting',     onStarting);
      socket.off('game:started',      onStarted);
      socket.off('game:launching',    onLaunching);
      socket.off('game:cashout:done', onCashoutDone);
    };
  },[socket,resetRoundVisuals,recordDiag,hydrateTrace]);

  const addDevSkier=useCallback(()=>{
    if(!devSimulation.active)return;
    const seq=devSeqRef.current++;
    const amount=Math.max(1,Number(devBet)||100);
    const username=(devName.trim()||`DevSkier${seq}`).slice(0,20);
    const playerId=devSimulation.addPlayer(username,amount);
    if(!playerId)return;
    setDevRows(prev=>[...prev,{playerId,username,betAmount:amount,status:'active',cashout:Math.max(1.01,currentMultiplier).toFixed(2)}]);
  },[devBet,devName,currentMultiplier,devSimulation]);

  const cashoutDevSkier=useCallback((playerId:string,multiplier:number)=>{
    if(!devSimulation.active)return;
    devSimulation.cashoutPlayer(playerId,multiplier);
    setDevRows(prev=>prev.map(row=>row.playerId===playerId?{...row,status:'cashed_out',cashedOutAt:multiplier}:row));
  },[devSimulation]);

  const updateDevCashout=useCallback((playerId:string,value:string)=>{
    setDevRows(prev=>prev.map(row=>row.playerId===playerId?{...row,cashout:value}:row));
  },[]);

  const clearDevSkiers=useCallback(()=>{
    if(devSimulation.active)devSimulation.clearPlayers();
    setDevRows([]);
  },[devSimulation]);

  const removeDevSkier=useCallback((playerId:string)=>{
    if(devSimulation.active)devSimulation.removePlayer(playerId);
    setDevRows(prev=>prev.filter(row=>row.playerId!==playerId));
  },[devSimulation]);

  useEffect(()=>{if(!devSimulation.active)setDevRows([]);},[devSimulation.active]);

  useEffect(()=>{
    const canvas=canvasRef.current;if(!canvas)return;
    const resizeCanvas=()=>{
      const fallback=getCanvasLogicalSize(window.innerWidth);
      const initialized=bgRef.current&&obsRef.current&&msRef.current&&crashEffectRef.current&&weatherRef.current;
      if(canvasProfileRef.current===fallback.profile&&initialized)return;
      // World dimensions must not depend on an intermediate layout measurement.
      // During breakpoint changes ResizeObserver can fire before CSS grid reflow has
      // settled, leaving a small backing buffer stretched across the final stage.
      const width=fallback.width;
      const height=fallback.height;
      canvas.width=width;canvas.height=height;
      canvasProfileRef.current=fallback.profile;
      bgRef.current=new BackgroundScroller(width,height);
      obsRef.current=new ObstacleManager(width,height,bgRef.current);
      msRef.current=new MultiSkierManager(width,height);
      crashEffectRef.current=new CrashEffectController(width,height);
      weatherRef.current=new RunWeatherEffect(width,height);
      if(seedHashRef.current){obsRef.current.initSeed(seedHashRef.current);msRef.current.initRound(seedHashRef.current);}
      msRef.current.setSoloFilter(soloModeRef.current&&playerRef.current?.id?playerRef.current.id:null);
      const info=giRef.current;
      if(info?.phase==='STARTING'){
        const didReset=resetRoundVisuals(roundKeyFromGameInfo(info)??`starting:${info.phaseStartTime}`,startingResetToken(info));
        hydrateRoundVisuals(info,didReset);
      }else if(info?.phase==='LAUNCHING'||info?.phase==='IN_PROGRESS')hydrateRoundVisuals(info,true);
    };
    resizeCanvas();
    let resizeRaf:number|null=null;
    const scheduleResize=()=>{
      if(resizeRaf!==null)cancelAnimationFrame(resizeRaf);
      resizeRaf=requestAnimationFrame(()=>{
        resizeRaf=requestAnimationFrame(()=>{resizeRaf=null;resizeCanvas();});
      });
    };
    const resizeObserver=typeof ResizeObserver!=='undefined'?new ResizeObserver(scheduleResize):null;
    if(viewportRef.current)resizeObserver?.observe(viewportRef.current);
    window.addEventListener('resize',scheduleResize,{passive:true});
    window.visualViewport?.addEventListener('resize',scheduleResize,{passive:true});
    const audio=sndRef.current;
    const onVis=()=>{
      if(document.visibilityState==='visible'){
        sndRef.current.handleVisible();
        // A backgrounded WebView has its timers throttled, so the local estimate
        // is untrustworthy on return: re-request the authoritative state.
        socketRef.current?.emit('game:resync');
        const info=giRef.current;
        if(info?.phase==='STARTING'){
          const didReset=resetRoundVisuals(roundKeyFromGameInfo(info)??`starting:${info.phaseStartTime}`,startingResetToken(info));
          hydrateRoundVisuals(info,didReset);
        }else if(info?.phase==='LAUNCHING'||info?.phase==='IN_PROGRESS'){
          clearCrashArtifacts();
          hydrateRoundVisuals(info,true);
        }
      }else{
        sndRef.current.handleHidden();
      }
    };
    document.addEventListener('visibilitychange',onVis);
    const resumeCtx=()=>sndRef.current.resumeAudio(true);
    document.addEventListener('click',resumeCtx,{once:false});document.addEventListener('keydown',resumeCtx);
    return()=>{resizeObserver?.disconnect();window.removeEventListener('resize',scheduleResize);window.visualViewport?.removeEventListener('resize',scheduleResize);if(resizeRaf!==null)cancelAnimationFrame(resizeRaf);document.removeEventListener('visibilitychange',onVis);document.removeEventListener('click',resumeCtx);document.removeEventListener('keydown',resumeCtx);audio.stopAll();resetRoster();};
  },[hydrateRoundVisuals,resetRoundVisuals,clearCrashArtifacts]);

  const triggerFlash=useCallback((source='unknown',details:Record<string,unknown>={})=>{if(reducedMotionRef.current)return;logShakeDiag(source,details);flashRef.current=1.0;sh.current=10;shA.current=10;shD.current=0.2;shFr.current=30;shFd.current=0.4;shS.current=performance.now();shL.current=performance.now();shFng.current=false;shTarg.current={x:(Math.random()*2-1)*10,y:(Math.random()*2-1)*10};},[logShakeDiag]);
  const updateShake=useCallback((now:number,ctx:CanvasRenderingContext2D)=>{if(sh.current<=0.01){sh.current=0;shOff.current={x:0,y:0};return;}const el=(now-shS.current)/1000,sl=(now-shL.current)/1000;if(shD.current>0&&el>=shD.current&&!shFng.current){shFng.current=true;shS.current=now;}if(shFng.current){const fp=Math.min((now-shS.current)/1000/shFd.current,1);sh.current=shA.current*(1-(Math.sin((fp-0.5)*Math.PI)+1)/2);if(fp>=1){sh.current=0;shOff.current={x:0,y:0};shFng.current=false;return;}}const fi=1/shFr.current;if(sl>=fi){shTarg.current={x:(Math.random()*2-1)*sh.current,y:(Math.random()*2-1)*sh.current};shL.current=now;}const ease=(Math.sin((Math.min(sl/fi,1)-0.5)*Math.PI)+1)/2;shOff.current={x:shOff.current.x+(shTarg.current.x-shOff.current.x)*ease,y:shOff.current.y+(shTarg.current.y-shOff.current.y)*ease};ctx.translate(shOff.current.x,shOff.current.y);},[]);

  const drawMultiplier=useCallback((ctx:CanvasRenderingContext2D,W:number,H:number)=>{
    const gi=giRef.current,mult=mRef.current;
    if(gi?.phase!=='IN_PROGRESS'&&gi?.phase!=='CRASHED')return;
    const now=performance.now();
    const staleSince=now-lmcRef.current,stale=gi?.phase==='IN_PROGRESS'&&staleSince>350;
    const alpha=stale ? .84 : 1;
    const crashed=gi?.phase==='CRASHED';
    const text=crashed?`WHITEOUT @ ${mult.toFixed(2)}x`:`${mult.toFixed(2)}x`;
    const y=Math.max(26,H-traceHeightFor(H)-84);
    const[r,g,b]=tierRGB(mult);
    const aurora=!crashed&&mult>=25;
    const celebration=aurora?getAuroraEscalation(mult):0;
    ctx.save();
    ctx.globalAlpha=alpha;
    ctx.font=fitFont(ctx,text,62,W*.9);
    ctx.textAlign='center';ctx.textBaseline='top';

    if(aurora){
      const pulse=.94+Math.sin(now*.0018)*.06;
      const haloRadius=84+celebration*34;
      const halo=ctx.createRadialGradient(W/2,y+33,0,W/2,y+33,haloRadius);
      halo.addColorStop(0,`rgba(255,245,197,${.2*pulse+celebration*.06})`);
      halo.addColorStop(.38,`rgba(116,246,230,${.12*pulse})`);
      halo.addColorStop(.72,`rgba(185,145,255,${.055+celebration*.025})`);
      halo.addColorStop(1,'rgba(145,195,255,0)');
      ctx.globalCompositeOperation='screen';ctx.fillStyle=halo;
      ctx.fillRect(W/2-haloRadius,y+33-haloRadius,haloRadius*2,haloRadius*2);

      if(celebration>=1){
        ctx.save();ctx.translate(W/2,y+33);ctx.scale(1.9,.58);
        ctx.strokeStyle=`rgba(255,229,157,${.2+Math.sin(now*.0013)*.045})`;
        ctx.lineWidth=1;ctx.setLineDash([7,10]);ctx.lineDashOffset=-now*.012;
        ctx.beginPath();ctx.arc(0,0,57,0,Math.PI*2);ctx.stroke();ctx.restore();
      }

      const textWidth=ctx.measureText(text).width;
      const fill=ctx.createLinearGradient(W/2-textWidth/2,y,W/2+textWidth/2,y+55);
      fill.addColorStop(0,'#f8ffff');fill.addColorStop(.3,'#8ff8e5');fill.addColorStop(.58,'#ffe29a');fill.addColorStop(.82,'#d8b9ff');fill.addColorStop(1,'#ffffff');
      ctx.lineWidth=1.2;ctx.strokeStyle='rgba(255,239,189,.72)';
      ctx.shadowColor='rgba(105,255,231,.64)';ctx.shadowBlur=24+celebration*10;ctx.shadowOffsetY=0;
      ctx.strokeText(text,W/2,y);ctx.fillStyle=fill;ctx.fillText(text,W/2,y);

      const sweep=((now/2600)%1);
      const glintX=W/2-textWidth/2-30+sweep*(textWidth+60);
      const glint=ctx.createLinearGradient(glintX-28,0,glintX+28,0);
      glint.addColorStop(0,'rgba(255,255,255,0)');glint.addColorStop(.45,'rgba(255,255,255,0)');glint.addColorStop(.5,`rgba(255,255,238,${.62+celebration*.18})`);glint.addColorStop(.55,'rgba(255,255,255,0)');glint.addColorStop(1,'rgba(255,255,255,0)');
      ctx.shadowBlur=8;ctx.shadowColor='rgba(255,240,177,.72)';ctx.fillStyle=glint;ctx.fillText(text,W/2,y);
    }else{
      ctx.fillStyle=crashed?'#ff5864':`rgb(${r},${g},${b})`;
      ctx.shadowColor='rgba(0,0,0,.9)';ctx.shadowBlur=26;ctx.shadowOffsetY=2;
      ctx.fillText(text,W/2,y);
    }
    ctx.restore();
  },[]);
  const drawLaunching=useCallback((ctx:CanvasRenderingContext2D,W:number,H:number)=>{if(giRef.current?.phase!=='LAUNCHING')return;ctx.save();ctx.font=fitFont(ctx,'DROP IN',68,W*.9);ctx.textAlign='center';ctx.textBaseline='top';ctx.fillStyle='#9cff57';ctx.shadowColor='rgba(0,0,0,0.9)';ctx.shadowBlur=28;ctx.fillText('DROP IN',W/2,Math.max(24,H-traceHeightFor(H)-94));ctx.restore();},[]);

  const drawTrace=useCallback((ctx:CanvasRenderingContext2D,W:number,H:number)=>{
    const gi=giRef.current,mult=mRef.current,phase=gi?.phase;
    if(phase==='IN_PROGRESS'){if(trStart.current===null){trStart.current=performance.now();trLastM.current=0;}if(mult!==trLastM.current){trData.current.push({t:(performance.now()-trStart.current)/1000,m:mult});trLastM.current=mult;if(trData.current.length>MAX_TRACE_PTS)trData.current.shift();}}
    if(phase==='IN_PROGRESS'||phase==='CRASHED')trOp.current=Math.min(1,trOp.current+0.05);else trOp.current=Math.max(0,trOp.current-0.025);
    const a=trOp.current;if(a<0.01||trData.current.length<2)return;
    const traceH=traceHeightFor(H),sTop=H-traceH,dTop=sTop+TRACE_PAD_TOP,dBot=H-TRACE_PAD_BOT,dL=TRACE_PAD_L,dR=W-TRACE_PAD_R,dW=dR-dL;
    const yMin=1.0;

    if(phase==='IN_PROGRESS') trYMaxRef.current=Math.max(trYMaxRef.current,mult*1.15,2.0);
    // Keep collecting history while the comparison switch hides the drawing.
    if(!showSlopeTraceRef.current)return;
    const yMax=trYMaxRef.current;

    const lastPt=trData.current[trData.current.length-1];
    const totSec=Math.max(lastPt?.t??0.1,0.1);
    const[r,g,b]=tierRGB(mult),ts=`${r},${g},${b}`;
    ctx.save();
    ctx.globalAlpha=a*0.75;ctx.fillStyle='rgba(4,8,14,0.93)';ctx.fillRect(0,sTop,W,traceH);
    ctx.globalAlpha=a*0.9;const bg2=ctx.createLinearGradient(0,0,W,0);bg2.addColorStop(0,'rgba(0,0,0,0)');bg2.addColorStop(0.05,`rgba(${ts},0.7)`);bg2.addColorStop(0.95,`rgba(${ts},0.7)`);bg2.addColorStop(1,'rgba(0,0,0,0)');ctx.strokeStyle=bg2;ctx.lineWidth=1;ctx.beginPath();ctx.moveTo(0,sTop);ctx.lineTo(W,sTop);ctx.stroke();
    ctx.setLineDash([3,7]);ctx.lineWidth=0.5;dynamicRefs(yMin,yMax).forEach(ref=>{const y=traceY(ref,yMin,yMax,dTop,dBot);if(y<=sTop||y>=H)return;ctx.globalAlpha=a*0.10;ctx.strokeStyle='#fff';ctx.beginPath();ctx.moveTo(dL,y);ctx.lineTo(dR,y);ctx.stroke();ctx.globalAlpha=a*0.28;ctx.fillStyle='#9aabb8';ctx.font='7.5px JetBrains Mono,monospace';ctx.textAlign='right';ctx.textBaseline='middle';ctx.fillText(`${ref}x`,dL-2,y);});ctx.setLineDash([]);
    const raw=trData.current,cnt=Math.min(raw.length,RENDER_PTS_MAX),stp=raw.length/cnt;
    const pts:{x:number;y:number;mult:number}[]=[{x:dL,y:traceY(1,yMin,yMax,dTop,dBot),mult:1}];
    for(let i=0;i<cnt;i++){const pt=raw[Math.min(Math.floor(i*stp),raw.length-1)];pts.push({x:dL+(pt.t/totSec)*dW,y:traceY(pt.m,yMin,yMax,dTop,dBot),mult:pt.m});}
    if(pts.length<2){ctx.restore();return;}const tip=pts[pts.length-1];
    const fg=ctx.createLinearGradient(0,dTop,0,dBot);fg.addColorStop(0,`rgba(${ts},0.24)`);fg.addColorStop(0.65,`rgba(${ts},0.07)`);fg.addColorStop(1,`rgba(${ts},0)`);
    ctx.globalAlpha=a;ctx.beginPath();ctx.moveTo(pts[0].x,dBot);ctx.lineTo(pts[0].x,pts[0].y);
    for(let i=1;i<pts.length-1;i++){const mx=(pts[i].x+pts[i+1].x)/2,my=(pts[i].y+pts[i+1].y)/2;ctx.quadraticCurveTo(pts[i].x,pts[i].y,mx,my);}
    ctx.lineTo(tip.x,tip.y);ctx.lineTo(tip.x,dBot);ctx.closePath();ctx.fillStyle=fg;ctx.fill();
    const lg=ctx.createLinearGradient(pts[0].x,0,tip.x,0);const s0:[number,number,number]=[0,216,127];
    for(let s=0;s<=6;s++){const t=s/6;const[lr,lg2,lb]=lerpRGB(s0,tierRGB(pts[Math.floor(t*(pts.length-1))].mult),t);lg.addColorStop(t,`rgba(${lr},${lg2},${lb},${0.55+t*0.45})`);}
    ctx.beginPath();ctx.moveTo(pts[0].x,pts[0].y);for(let i=1;i<pts.length-1;i++){const mx=(pts[i].x+pts[i+1].x)/2,my=(pts[i].y+pts[i+1].y)/2;ctx.quadraticCurveTo(pts[i].x,pts[i].y,mx,my);}ctx.lineTo(tip.x,tip.y);
    ctx.strokeStyle=lg;ctx.lineWidth=2.5;ctx.lineJoin='round';ctx.stroke();
    const halo=ctx.createRadialGradient(tip.x,tip.y,0,tip.x,tip.y,10);halo.addColorStop(0,`rgba(${ts},0.42)`);halo.addColorStop(0.5,`rgba(${ts},0.12)`);halo.addColorStop(1,`rgba(${ts},0)`);ctx.fillStyle=halo;ctx.beginPath();ctx.arc(tip.x,tip.y,10,0,Math.PI*2);ctx.fill();
    ctx.fillStyle=`rgb(${ts})`;ctx.beginPath();ctx.arc(tip.x,tip.y,3.5,0,Math.PI*2);ctx.fill();
    ctx.fillStyle='rgba(255,255,255,0.7)';ctx.beginPath();ctx.arc(tip.x-1,tip.y-1,1.2,0,Math.PI*2);ctx.fill();
    ctx.globalAlpha=a*0.5;ctx.fillStyle=`rgb(${ts})`;ctx.font='700 7.5px JetBrains Mono,monospace';ctx.textAlign='left';ctx.textBaseline='middle';ctx.fillText('▲ SPEED',4,sTop+traceH/2);
    ctx.globalAlpha=a;ctx.fillStyle=`rgb(${ts})`;ctx.font='bold 11px Barlow Condensed,Arial';ctx.textAlign='right';ctx.textBaseline='middle';ctx.fillText(`${mult.toFixed(2)}x`,W-2,Math.max(dTop+5,Math.min(dBot-5,tip.y)));
    ctx.restore();
  },[]);

  useEffect(()=>{
    const canvas=canvasRef.current,ctx=canvas?.getContext('2d');if(!canvas||!ctx)return;
    if(!bgRef.current||!obsRef.current||!msRef.current||!weatherRef.current)return;
    lfRef.current=0;
    const loop=(ts:number)=>{
      const bg=bgRef.current,obs=obsRef.current,ms=msRef.current,weather=weatherRef.current;
      if(!bg||!obs||!ms||!weather){afRef.current=requestAnimationFrame(loop);return;}
      const dt=lfRef.current===0?16.667:Math.min(ts-lfRef.current,100);lfRef.current=ts;
      ctx.clearRect(0,0,canvas.width,canvas.height);ctx.resetTransform();
      if(sh.current>0)updateShake(performance.now(),ctx);
      const gi=giRef.current,mult=frameMultiplier();
      mRef.current=mult;
      const runTier=getRunTier(mult);
      if(tierImpulseRef.current>.04){
        const impulse=tierImpulseRef.current;
        ctx.translate(Math.sin(ts*.052)*impulse,-Math.cos(ts*.041)*impulse*.28);
        tierImpulseRef.current*=Math.pow(.9,dt/16.667);
      }
      const supersededCrashFrame=isSupersededCrashFrame(gi,visualRoundKeyRef.current);
      const crashed=gi?.phase==='CRASHED'&&!supersededCrashFrame,phase=supersededCrashFrame?'STARTING':gi?.phase;
      const elapsedSeconds=(phase==='IN_PROGRESS'&&roundStartTimeRef.current)?Math.max(0,(Date.now()-roundStartTimeRef.current)/1000):0;
      weather.update(reducedMotionRef.current?0:dt,runTier,phase==='IN_PROGRESS'||crashed,getRunTierProgress(mult),mult);

      if(gi&&phase!=='CRASHED'){
        const key=roundKeyFromGameInfo(gi)??(phase==='STARTING'?`starting:${gi.phaseStartTime}`:null);
        const resetToken=phase==='STARTING'?startingResetToken(gi):null;
        if(phase==='STARTING'&&key!==null&&visualResetTokenRef.current!==resetToken){
          const didReset=resetRoundVisuals(key,resetToken);
          hydrateRoundVisuals(gi,didReset);
        }else{
          if(crashRef.current||postCrashClean.current||postCrashTimer.current||sh.current>0)clearCrashArtifacts();
          const canonicalRoster=rosterIdsFromGameInfo(gi);
          if(!ms.matchesRoster(canonicalRoster)){
            recordDiag('repair:roster-mismatch',{
              canonicalRoster,
              visualRoster:ms.getAllPlayerIds(),
              obstacleCount:obs.getCount(),
            });
            ms.reset();
            if(seedHashRef.current)ms.initRound(seedHashRef.current);
            hydrateRoundVisuals(gi,true);
          }
        }
      }

      if(phase==='IN_PROGRESS'){
        sndRef.current.startSkiLoop(bg.getCurrentSpeed());
        sndRef.current.setRunTier(runTier.index);
        obs.resume();bg.setSpeed(raceScrollSpeedForMultiplier(mult));bg.update(dt);obs.update(dt);obs.setDifficulty(mult);
        ms.update(obs.getObstacles(),bg,elapsedSeconds);showingWinner.current=false;winnerAlpha.current=0;
      }else if(phase==='STARTING'){
        bg.resume();obs.pause();bg.setSpeed(startingPhaseSpeed(gi?.phaseStartTime));bg.update(dt);ms.update(obs.getObstacles(),bg,0);
      }else if(phase==='LAUNCHING'){
        if(!launchTriggeredRef.current){launchTriggeredRef.current=true;ms.beginLaunch();}
        bg.resume();obs.pause();bg.setSpeed(ROUND_START_SPEED);
        bg.update(dt);ms.update(obs.getObstacles(),bg,0);
      }else if(crashed){
        if(!crashRef.current){
          const presentation=devCrashPresentationRef.current;
          activeCrashPresentationRef.current=presentation;
          crashRef.current=true;postCrashClean.current=false;obs.pause();
          if(presentation==='cliff'){
            bg.setSpeedImmediate(Math.max(0.35,bg.getCurrentSpeed()));bg.resume();
          }else{
            bg.setSpeed(0);bg.pause();
          }
          crashEffectRef.current?.begin(presentation);
          ms.beginCrashSequence(presentation);sndRef.current.playSFX('crash');sndRef.current.stopSkiLoop();sndRef.current.resetRunTier();triggerFlash('render:crashed:first-frame',{
            roundKey:roundKeyFromGameInfo(gi),
            crashMultiplier:mult,
            crashPresentation:presentation,
            skierTotal:ms.getTotalCount(),
            skierVisible:ms.getVisibleCount(),
            betCount:(gi?.bets??[]).length,
          });
          if(postCrashTimer.current)clearTimeout(postCrashTimer.current);
          const crashRoundKey=roundKeyFromGameInfo(gi);
          postCrashRoundKeyRef.current=crashRoundKey;
          postCrashTimer.current=setTimeout(()=>{
            const current=giRef.current;
            if(current?.phase==='CRASHED'&&roundKeyFromGameInfo(current)===crashRoundKey)postCrashClean.current=true;
          },5000);
        }
        crashEffectRef.current?.update(dt);
        if(crashEffectRef.current?.shouldScrollWorld()){
          bg.update(dt);obs.followBackground();
        }else if(activeCrashPresentationRef.current==='cliff'){
          bg.pause();
        }
        ms.update(obs.getObstacles(),bg,elapsedSeconds);
        if(postCrashClean.current&&roundKeyFromGameInfo(gi)===postCrashRoundKeyRef.current){
          ms.reset();obs.clear();crashEffectRef.current?.reset();winnerAlpha.current=0;showingWinner.current=false;postCrashClean.current=false;postCrashRoundKeyRef.current=null;
        }
        if(!soloModeRef.current&&ms.winner&&!ms.hasAnyRacers()){
          showingWinner.current=true;
          winnerAlpha.current=Math.min(1,winnerAlpha.current+0.018);
        }
      }

      bg.draw(ctx);weather.drawBack(ctx,runTier);ms.drawTracks(ctx,mult);obs.draw(ctx);crashEffectRef.current?.drawUnder(ctx);ms.drawBodies(ctx,mult);weather.drawFront(ctx,runTier);
      crashEffectRef.current?.drawOver(ctx);
      if(flashRef.current>0){ctx.save();ctx.fillStyle=`rgba(255,255,255,${flashRef.current})`;ctx.fillRect(0,0,canvas.width,canvas.height);ctx.restore();flashRef.current-=3.0*(dt/1000);if(flashRef.current<0)flashRef.current=0;}
      if(!supersededCrashFrame){drawTrace(ctx,canvas.width,canvas.height);drawMultiplier(ctx,canvas.width,canvas.height);}
      drawLaunching(ctx,canvas.width,canvas.height);

      // The ranking lives in the Live bets panel / phone strip, off the slope; the
      // canvas only shares what it knows (order, skier colours, queue).
      if(!soloModeRef.current&&ms.getTotalCount()>0)
        publishRoster(ms.getLeaderboard(mult).map(e=>({playerId:e.playerId,username:e.username,chroma:e.chroma,status:e.status,betAmount:e.betAmount,cashoutMultiplier:e.cashoutMultiplier})),ms.getQueueCount());
      else resetRoster();
      if(!soloModeRef.current&&showingWinner.current&&winnerAlpha.current>0)
        ms.drawWinnerBanner(ctx,canvas.width,winnerAlpha.current);

      afRef.current=requestAnimationFrame(loop);
    };
    const startLoop=()=>{if(afRef.current==null){lfRef.current=0;afRef.current=requestAnimationFrame(loop);}};
    const stopLoop=()=>{if(afRef.current!=null){cancelAnimationFrame(afRef.current);afRef.current=null;}};
    const onVisibility=()=>{if(document.visibilityState==='visible')startLoop();else stopLoop();};
    document.addEventListener('visibilitychange',onVisibility);
    if(document.visibilityState==='visible')startLoop();
    return()=>{document.removeEventListener('visibilitychange',onVisibility);stopLoop();};
  },[drawMultiplier,drawLaunching,triggerFlash,updateShake,drawTrace,resetRoundVisuals,hydrateRoundVisuals,clearCrashArtifacts,recordDiag,frameMultiplier]);

  const activeRunTier=getRunTier(currentMultiplier);

  return(
    <div className="canvas-game" style={{background:'#0a0e14',borderRadius:'inherit',overflow:'hidden'}}>
      <div ref={viewportRef} className={`relative overflow-hidden canvas-viewport atmosphere-${activeRunTier.id}`}>
        <canvas ref={canvasRef} className="w-full h-full" style={{display:'block'}}/>
        {tierTransition&&<div key={tierTransition.key} className={`tier-transition tier-${tierTransition.tier.id}`} style={{'--tier-color':tierTransition.tier.color} as React.CSSProperties} aria-live={showRunLevelTitles?'polite':undefined} aria-hidden={showRunLevelTitles?undefined:true}>
          <div className="tier-transition-snow" />
          {showRunLevelTitles&&<div className="tier-transition-copy"><span>Run level {String(tierTransition.tier.index+1).padStart(2,'0')}</span><strong>{tierTransition.tier.name}</strong><small>{tierTransition.tier.description}</small></div>}
        </div>}
        <div className="absolute inset-y-0 left-0 pointer-events-none" style={{width:56,zIndex:5,background:'linear-gradient(to right,rgba(10,14,20,0.70),transparent)'}}/>
        <div className="absolute inset-y-0 right-0 pointer-events-none" style={{width:56,zIndex:5,background:'linear-gradient(to left,rgba(10,14,20,0.70),transparent)'}}/>
        <div className="absolute top-0 left-0 right-0 pointer-events-none" style={{height:40,zIndex:4,background:'linear-gradient(to bottom,rgba(10,14,20,0.48),transparent)'}}/>
      </div>
      <RunTelemetry phase={gameInfo?.phase} hasActiveBet={myBet.isActive} hasCashedOut={myBet.cashedOut} cashoutMultiplier={myBet.cashoutMultiplier} activeBetAmt={myBet.amount} startingAt={startingAt} showRunLevelGraph={showRunLevelGraph}/>
      {isDevAdmin&&(
        <DevSkierAdmin rows={devRows} name={devName} bet={devBet} currentMultiplier={currentMultiplier} onName={setDevName} onBet={setDevBet} onAdd={addDevSkier} onCashout={cashoutDevSkier} onCashoutInput={updateDevCashout} onClear={clearDevSkiers} minimized={devPanelMinimized} onToggleMinimized={()=>setDevPanelMinimized(v=>!v)} crashPresentation={devCrashPresentation} onCrashPresentation={selectDevCrashPresentation} simulationActive={devSimulation.active} scenarioRunning={devSimulation.scenarioRunning} phase={gameInfo?.phase} onEnterSimulation={()=>{setDevRows([]);devSimulation.enter();}} onExitSimulation={devSimulation.exit} onNewRound={()=>{setDevRows([]);devSimulation.newRound();}} onPhase={devSimulation.setPhase} onMultiplier={devSimulation.setMultiplier} onTriggerCrash={()=>devSimulation.triggerCrash(currentMultiplier)} onRunLifecycle={()=>{setDevRows([]);devSimulation.runScenario('lifecycle');}} onLoadLateRun={()=>{setDevRows([]);devSimulation.runScenario('late-run');}} onStopScenario={devSimulation.stopScenario} onRemove={removeDevSkier}/>
      )}
    </div>
  );
};

export default CanvasGame;
